import React, {
  HTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { FIELD_STORED, useTable } from './use-synchronize';
import { FieldForSync, useFieldsForMapping } from './synchronize-context';
import { map } from 'rxjs/operators';
import Table from '../../../../../../components/common/table/table';
import css from './tables.module.css';
import Button from './button';
import ReactTooltip from 'react-tooltip';
import { nanoid } from '@reduxjs/toolkit';

export interface FieldForMapping extends FieldForSync {
  stored?: ReactNode | keyof typeof FIELD_STORED;
  first_local_field: boolean;
  last_local_field: boolean;
  editable: ReactNode;
  isCheckable?: boolean;
  group_id?: string;
  key: string;
}

const getClassNames = (row: FieldForMapping) => {
  if (!row.id && row.isCheckable) {
    return css['default'];
  }

  const isLocalFieldSplittedOrMerged = row.id && row.group_id;

  if (isLocalFieldSplittedOrMerged) {
    const classNames = [
      'colored',
      'not-checkable',
      row.first_local_field ? 'colored-first' : '',
      row.last_local_field ? 'border' : '',
    ];

    return classNames.map((className) => css[className]).join(' ');
  }

  return `${css['not-checkable']} ${css['not-editable']} ${css['default']}`;
};

const tooltipContainerProps = {
  'data-for': 'tooltip',
  'data-tip': true,
} as HTMLAttributes<HTMLDivElement>;

export default function MappingTable() {
  const { t, tp, farmId, season, fieldService, selectedFields } =
    useTable<FieldForMapping>();
  const {
    setMappingState,
    setFieldSearchForMappingState,
    setCurrentLadStatusWithLadState,
    mappingState: { state, checked: mappingStateChecked },
    currentLadStatusWithLadState,
  } = useFieldsForMapping();
  const [hasNoFields, setHasNoFields] = useState(false);

  const [checked] = selectedFields;

  useEffect(() => {
    if (
      checked.length === 1 &&
      mappingStateChecked.some((field) => !field.isCheckable)
    ) {
      setFieldSearchForMappingState((prevState) => ({
        ...prevState,
        checked: [],
      }));
    }
  }, [checked.length, mappingStateChecked, setFieldSearchForMappingState]);

  useEffect(() => {
    setMappingState((prevState) => ({ ...prevState, checked }));

    if (checked.length === 0) {
      setFieldSearchForMappingState((prevState) => ({
        ...prevState,
        checked: [],
      }));
    }
  }, [checked, setFieldSearchForMappingState, setMappingState]);

  const request = useCallback(
    () =>
      fieldService.ladFieldsThatNotMatch(farmId, season).pipe(
        map((request) => {
          if (request.data.length === 0) {
            setHasNoFields(true);
          }

          const fields = request.data.map((field) => ({
            ...field,
            key: nanoid(),
            editable: null,
            isCheckable: true,
            stored: FIELD_STORED.LAD,
            first_local_field: false,
            last_local_field: false,
          }));

          const filteredFields = fields.filter(
            ({ geometry }) =>
              !currentLadStatusWithLadState.state.some(
                (field) => field.geometry === geometry
              )
          );

          return {
            ...request,
            data: [
              ...state,
              ...filteredFields.filter(
                ({ geometry }) =>
                  !state.some((field) => field.geometry === geometry)
              ),
            ],
          };
        })
      ),
    [currentLadStatusWithLadState.state, farmId, fieldService, season, state]
  );

  const saveButtonIsDisabled =
    state.filter((field) => field.isCheckable).length === state.length;

  const onSaveButtonClick = () => {
    setMappingState((prevState) => ({
      ...prevState,
      state: state.filter((field) => !field.group_id),
    }));

    setCurrentLadStatusWithLadState((prevState) => ({
      ...prevState,
      state: [...prevState.state, ...state.filter((field) => field.group_id)],
    }));
  };

  const saveButton = (
    <Button
      disabled={saveButtonIsDisabled}
      text={t('save')}
      onClick={onSaveButtonClick}
    />
  );

  const onMouseEnter = (row: FieldForMapping) => {
    setMappingState((prevState) => ({ ...prevState, hovered: row }));
  };

  const onMouseLeave = () => {
    setMappingState((prevState) => ({ ...prevState, hovered: null }));
  };

  const onRowClick = (field: FieldForMapping) => {
    if (field.editable && field.group_id) {
      setFieldSearchForMappingState((prevState) => ({
        ...prevState,
        checked: state
          .filter(
            ({ group_id, stored }) =>
              group_id === field.group_id && stored !== FIELD_STORED.LAD
          )
          .map(
            ({
              stored,
              first_local_field,
              last_local_field,
              editable,
              isCheckable,
              ...field
            }) => field
          ),
      }));

      setMappingState((prevState) => ({
        ...prevState,
        checked: state.filter(
          ({ group_id, stored }) =>
            group_id === field.group_id && stored === FIELD_STORED.LAD
        ),
      }));
    }

    return;
  };

  const tooltip = (
    <ReactTooltip
      id="tooltip"
      backgroundColor="#002A58"
      place="top"
      effect="solid"
    >
      {tp('check_one_or_more_fields')}
    </ReactTooltip>
  );

  const table = (
    <div className={`flex flex-col ${css['mapping-table']}`}>
      <p className="px-4 py-4 pb-0">{tp('fields_difference_over_threshold')}</p>
      <Table<FieldForMapping>
        tooltipContainerProps={tooltipContainerProps}
        tooltip={tooltip}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        request={request}
        keyAccessor="key"
        paginate={false}
        selectedRowsState={selectedFields}
        rowClassSetter={getClassNames}
        columns={[
          {
            accessor: 'stored',
            className: 'text-accent',
            header: '',
          },
          {
            accessor: 'lad_number',
            header: tp('lad_number'),
          },
          {
            accessor: 'name',
            header: tp('name'),
          },
          {
            accessor: 'block_number',
            header: tp('block_number'),
          },
          {
            accessor: 'area',
            header: tp('area'),
          },
          {
            accessor: 'crop_value',
            header: tp('crop'),
          },
          {
            accessor: 'editable',
            header: '',
            onRowClick,
          },
        ]}
      />
      <div className="self-end p-4 pt-0">{saveButton}</div>
    </div>
  );

  if (hasNoFields && state.length === 0) {
    setHasNoFields(false);
    return null;
  }

  return table;
}
